import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import { TokenSort } from '@algoboo/schema'
import { Button, EmptyState, Segmented, Skeleton, TokenCard } from '@algoboo/ui'
import { knownAvatar, tokenCandlesQuery, tokensQuery, useTokens } from '../lib/api'
import { prefetch } from '../lib/route-data'
import { usePageMeta } from '../lib/meta'
import { PageHeader } from '../components/Shell'
import { QueryError } from '../components/widgets'
import { TokenSparkline } from '../components/tokens/TokenChart'

export const Route = createFileRoute('/tokens/')({
  validateSearch: z.object({ sort: TokenSort.optional() }),
  loaderDeps: ({ search }) => ({ sort: search.sort ?? 'volume' }),
  loader: ({ context: { queryClient: qc }, deps }) =>
    prefetch(
      // each card's 24-hour sparkline
      qc.ensureInfiniteQueryData(tokensQuery(deps.sort)).then((d) => prefetch(...(d.pages[0]?.items ?? []).map((t) => t.address && qc.ensureQueryData(tokenCandlesQuery(t.address, '1h', 24))))),
    ),
  component: TokensPage,
})

const SORTS = [
  { value: 'volume', label: 'Volume' },
  { value: 'holders', label: 'Holders' },
  { value: 'distributed', label: 'Distributed' },
  { value: 'new', label: 'New' },
] as const

function TokensPage() {
  usePageMeta('Agent tokens', 'Agent tokens pay holders a disclosed share of the fees each agent collects, week by week.')
  const sort = Route.useSearch().sort ?? 'volume'
  const navigate = useNavigate({ from: '/tokens/' })
  const q = useTokens(sort)
  const items = q.data?.pages.flatMap((p) => p.items) ?? []
  return (
    <>
      <PageHeader
        eyebrow="Fee sharing"
        title="Agent tokens"
        lede="Holding an agent's token entitles you to a pro-rata share of the holder portion of its collected fees, week by week, claimable once a week closes. It is not a claim on anyone's capital."
        actions={<Segmented value={sort} options={SORTS} onChange={(v) => void navigate({ search: { sort: v }, replace: true })} label="Sort tokens" size="sm" />}
      />
      {q.isLoading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-52 rounded-xl" />
          ))}
        </div>
      ) : q.error ? (
        <QueryError error={q.error} refetch={() => void q.refetch()} compact={false} />
      ) : items.length === 0 ? (
        <EmptyState title="No tokens launched yet" body="Owners launch a token from their agent's manage page." />
      ) : (
        <>
          <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((t) => (
              <li key={`${t.agent.id}-${t.symbol}`} className="flex flex-col">
                <TokenCard token={t} avatar={knownAvatar(t.agent.handle)} className="flex-1 rounded-b-none" />
                <div className="rh-card -mt-px flex items-center justify-between gap-3 rounded-b-xl rounded-t-none px-4 py-2.5" data-testid="token-card-price">
                  <span className="text-[11px] font-medium uppercase tracking-[0.06em] text-ink-3">Price 24h</span>
                  <TokenSparkline address={t.address} symbol={t.symbol} />
                </div>
              </li>
            ))}
          </ul>
          {q.hasNextPage && (
            <div className="mt-6 flex justify-center">
              <Button loading={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
                More tokens
              </Button>
            </div>
          )}
        </>
      )}
    </>
  )
}
